import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Alert,
    Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RepeatIcon from '@mui/icons-material/Repeat';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AddIcon from '@mui/icons-material/Add';
import { getDeviceSchedules } from '../../api_calls/devices';
import dayjs from 'dayjs';

const ScheduleModal = ({ open, onClose, device, onEditSchedule, onCreateSchedule }) => {
    const [schedules, setSchedules] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open && device?.device_id) {
            fetchSchedules();
        } else {
            setSchedules(null);
            setError(null);
        }
    }, [open, device?.device_id]);

    const fetchSchedules = () => {
        if (!device?.device_id) return;
        
        setLoading(true);
        setError(null);
        getDeviceSchedules(device.device_id)
            .then((response) => {
                const scheduleData = response.data;
                if (scheduleData && scheduleData.jobs) {
                    // Filter out disabled schedules and format them
                    const enabledSchedules = scheduleData.jobs
                        .filter(job => job.enabled)
                        .map(job => ({
                            ...job,
                            formattedDate: job.recurring 
                                ? 'Everyday' 
                                : dayjs(job.timestamp).format('MMM D, YYYY'),
                            formattedTime: dayjs(job.timestamp).format('HH:mm'),
                            duration: job.duration || '0m'
                        }));
                    setSchedules(enabledSchedules);
                } else {
                    setSchedules([]);
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error('Error fetching schedules:', err);
                setError('Failed to load schedules');
                setLoading(false);
            });
    };

    const handleScheduleClick = (schedule) => {
        if (onEditSchedule) {
            onEditSchedule(schedule);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            fullWidth 
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ 
                            bgcolor: 'primary.main', 
                            borderRadius: 2, 
                            p: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <CalendarTodayIcon sx={{ color: 'white', fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Typography variant="h6">Schedules</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {device?.name}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : schedules && schedules.length > 0 ? (
                    <List>
                        {schedules.map((schedule, index) => (
                            <ListItem
                                key={index}
                                onClick={() => handleScheduleClick(schedule)}
                                sx={{
                                    borderRadius: 2,
                                    mb: 1,
                                    cursor: 'pointer',
                                    bgcolor: 'grey.50',
                                    '&:hover': {
                                        bgcolor: 'grey.100',
                                    },
                                }}
                            >
                                <ListItemIcon>
                                    {schedule.recurring ? (
                                        <RepeatIcon sx={{ color: 'primary.main' }} />
                                    ) : (
                                        <CalendarTodayIcon sx={{ color: 'primary.main' }} />
                                    )}
                                </ListItemIcon>
                                <ListItemText
                                    primary={schedule.formattedDate}
                                    secondary={
                                        <>
                                            Turn on at {schedule.formattedTime}
                                            <br />
                                            Duration: {schedule.duration}
                                        </>
                                    }
                                />
                                <ArrowForwardIosIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        No schedules found. Create a new schedule to get started.
                    </Alert>
                )}
            </DialogContent>
            
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    onClick={() => {
                        if (onCreateSchedule) {
                            onCreateSchedule();
                        }
                    }}
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{ textTransform: 'none' }}
                >
                    Create Schedule
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ScheduleModal;

